import { Button } from "@radix-ui/themes";
import { CheckIcon, CopyIcon } from "@radix-ui/react-icons";
import { useCopyMarkdown } from "~/hooks/use-copy-markdown";

/**
 * A button component that copies markdown content to clipboard.
 * Supports both HTML and plain text clipboard formats.
 *
 * @param content - The markdown content to copy
 * @param newsProvider - The news provider for URL rewriting (default: "mtnews")
 * @param className - Additional CSS classes (default: "mt-1")
 *
 * @example
 * ```tsx
 * // Basic usage
 * <CopyButton content={markdownContent} />
 *
 * // With custom provider
 * <CopyButton content={markdownContent} newsProvider="dow_jones" />
 *
 * // With custom styling
 * <CopyButton content={markdownContent} className="mt-4" />
 * ```
 */
export function CopyButton({
  content,
  newsProvider = "mtnews",
  className = "mt-1",
}: {
  content: string;
  newsProvider?: string;
  className?: string;
}) {
  const { copied, copyToClipboard } = useCopyMarkdown(newsProvider);

  return (
    <Button
      variant="surface"
      size="1"
      onClick={() => copyToClipboard(content)}
      className={className}
    >
      {copied ? <CheckIcon color="green" /> : <CopyIcon />}
      <span className="ml-1">{copied ? "Copied" : "Copy"}</span>
    </Button>
  );
}
